import { useState, useEffect } from 'react';
import { workOrderAPI, assignmentAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Zap, ListTodo } from 'lucide-react';
import { toast } from 'sonner';
import WorkOrderCard from './WorkOrderCard';

export default function WorkOrderQueue({ user }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const canEdit = user?.role === 'Administrator' || user?.role === 'Manager';

  const fetchData = async () => {
    try {
      setLoading(true);
      const woRes = await workOrderAPI.getAll();
      setWorkOrders(woRes.data);

      const assignRes = await assignmentAPI.getAll(
        new Date().toISOString().split('T')[0],
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      );

      const assignedIds = new Set(assignRes.data.map((a) => a.work_order_id));
      setUnassigned(woRes.data.filter((wo) => !assignedIds.has(wo.id)));
    } catch (error) {
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (workOrderId) => {
    setWorkOrders(prev => prev.filter(wo => wo.id !== workOrderId));
    setUnassigned(prev => prev.filter(wo => wo.id !== workOrderId));
    toast.success('Work order deleted successfully');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSync = async () => {
    try {
      await workOrderAPI.sync();
      toast.success('Work orders synced from NetSuite');
      fetchData();
    } catch (error) {
      toast.error('Sync failed');
    }
  };

  const handleAutoAssign = async () => {
    if (unassigned.length === 0) {
      toast.info('No unassigned work orders');
      return;
    }

    try {
      const ids = unassigned.map((wo) => wo.id);
      await assignmentAPI.autoAssign(ids);
      toast.success('Work orders auto-assigned');
      fetchData();
    } catch (error) {
      toast.error('Auto-assign failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Loading work orders...</div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="work-order-queue">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Work Order Queue</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage production work orders</p>
        </div>
        {canEdit && (
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSync} data-testid="sync-queue-button">
              <RefreshCw className="mr-2 h-4 w-4" />
              Sync NetSuite
            </Button>
            <Button
              onClick={handleAutoAssign}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="auto-assign-queue-button"
            >
              <Zap className="mr-2 h-4 w-4" />
              Auto-Assign All
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge variant="destructive" className="h-6 w-6 rounded-full p-0 flex items-center justify-center">
                {unassigned.length}
              </Badge>
              Unassigned Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[600px] overflow-y-auto scrollbar-hide">
              {unassigned.map((wo) => (
                <WorkOrderCard key={wo.id} workOrder={wo} isDraggable={false} onDelete={handleDelete} user={user} />
              ))}
              {unassigned.length === 0 && (
                <div className="text-center py-12">
                  <ListTodo className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">All work orders assigned</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge className="h-6 w-6 rounded-full p-0 flex items-center justify-center bg-emerald-500">
                {workOrders.length - unassigned.length}
              </Badge>
              Assigned Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[600px] overflow-y-auto scrollbar-hide">
              {workOrders
                .filter((wo) => !unassigned.find((u) => u.id === wo.id))
                .map((wo) => (
                  <WorkOrderCard key={wo.id} workOrder={wo} isDraggable={false} onDelete={handleDelete} user={user} />
                ))}
              {workOrders.length === unassigned.length && (
                <div className="text-center py-12">
                  <ListTodo className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No assigned work orders</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6 shadow-sm">
        <CardHeader>
          <CardTitle>All Work Orders ({workOrders.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {workOrders.map((wo) => (
              <WorkOrderCard key={wo.id} workOrder={wo} isDraggable={false} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
